<script>
  import Chart from "svelte-chartjs/src/Bar.svelte";
  import { onMount } from "svelte";
  import { DAY, generateCanvasGradient, makeWebsiteRequest, parsePercent, months, monthsShort } from "../Common/tools";
  import {External, M} from "../external";

  export let funnel = null,
    period = null;

  let grouping = "monthly";
  let steps = [];
  let stepSummaries = [];

  const gradientColors = [
    ["#FF8787", "#FF8787"],
    ["#3da6ff", "#257cc6"],
    ["#ff9800", "#ffab2e"],
    ["#ffd800", "#ffd800"],
    ["#bcc75b", "#bcc75b"],
    ["#a2ff00", "#a2ff00"],
    ["#6ad87c", "#6ad87c"],
    ["#6ad8b2", "#6ad8b2"],
    ["#6accd8", "#6accd8"],
    ["#6ab0d8", "#6ab0d8"],
    ["#6a93d8", "#6a93d8"],
    ["#6a6fd8", "#6a6fd8"],
    ["#8c6ad8", "#8c6ad8"],
    ["#ba6ad8", "#ba6ad8"],
    ["#d86ad4", "#d86ad4"],
    ["#ea2d8f", "#ea2d8f"],
    ["#df4157", "#df4157"],
    ["#6fc385", "#6fc385"],
    ["#bec36f", "#bec36f"],
    ["#c3936f", "#c3936f"]
  ];

  let chartDataLabels = [];
  let monthLabels = [];
  let showMonthsBelow = false;
  let chartWrapper = null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: false,
      title: false,
      tooltip: {
        mode: "index",
        intersect: false,
        displayColors: false
      }
    },
    scales: {
      xAxes: {
        display: false
      },
      yAxes: {
        display: false
      }
    },
    layout: {
      padding: 3
    }
  };

  let chartData = {
    labels: [],
    datasets: []
  };

  let stats = {};

  let updateGrouping = () => {
    let startTime = period.from || new Date(parseInt(External.createdTime) * 1000);
    let endTime = period.to || new Date();
    startTime.setHours(0, 0, 0, 0);
    endTime.setHours(23, 59, 59, 999);
    let diff = Math.abs(endTime.getTime() - startTime.getTime());
    if (diff <= 31 * DAY) {
      grouping = "daily";
    } else if (diff < 558 * DAY) {
      // 18 months
      grouping = "monthly";
    } else {
      grouping = "yearly";
    }
  };

  export async function reload() {
    updateGrouping();
    let from = External.Tools.dateToISOStr(period && period.from ? period.from : new Date(parseInt(External.createdTime) * 1000));
    let to = External.Tools.dateToISOStr(period && period.to ? period.to : new Date());

    let variations = [];
    steps.forEach((step) => {
      let variantIds = step.variants.map((variant) => variant.id);
      variations.push(...variantIds);
    });

    let response = await makeWebsiteRequest({
      action: "getDetailedStats",
      method: "post",
      data: {
        grouping: grouping,
        funnelId: funnel.id,
        from: from,
        to: to,
        variations: JSON.stringify(variations)
      }
    });

    if (!response.data) {
      return;
    }
    stats = response.data || {};

    chartDataLabels = Object.keys(stats[steps[0].variants[0].id]);
    monthLabels = [];
    showMonthsBelow = chartDataLabels.length > (chartWrapper.offsetWidth > 610 ? 15 : 8);
    chartDataLabels = chartDataLabels.map((label) => {
      let date;
      switch (grouping) {
        case "daily":
          date = new Date(label);
          if (showMonthsBelow) {
            let month = months[date.getMonth()];
            if (monthLabels.length === 0 || monthLabels[monthLabels.length - 1] !== month) {
              monthLabels.push(month);
            }
            return date.getDate();
          } else {
            return External.WebPlatform.dateToStrWithMonth(date, true, false, true, monthsShort);
          }
        case "monthly":
          date = new Date(label);
          return External.WebPlatform.dateToStrWithMonth(date, false, false, false, monthsShort);
        case "yearly":
          return label;
      }
      return label;
    });

    let canvas = document.querySelector(".analytics-box-big canvas");

    let datasets = [];
    steps.forEach((step, stepIdx) => {
      let gradient = generateCanvasGradient(canvas, gradientColors[stepIdx] || gradientColors[gradientColors.length - 1]);
      let dataset;
      if (stepIdx == 0) {
        dataset = {
          label: M.TOTAL_VISITORS,
          data: [],
          backgroundColor: gradient,
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: true,
          borderRadius: 3,
          borderColor: "transparent",
          order: 1 + steps.length
        };
      } else {
        dataset = {
          label: "Goal " + stepIdx,
          data: [],
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: true,
          borderRadius: 3,
          borderColor: gradient,
          type: "line",
          order: 0 + steps.length - stepIdx
        };
      }

      step.variants.forEach((variant) => {
        let variantStats = stats[variant.id];
        let dataPoints = Object.values(variantStats);
        if (dataset.data.length === 0) {
          dataset.data = dataPoints.map((stat) => stat.sessions);
        } else {
          dataPoints.forEach((stat, idx) => {
            dataset.data[idx] = (dataset.data[idx] || 0) + stat.sessions;
          });
        }
      });

      datasets.push(dataset);
    });

    stepSummaries = [];
    datasets.forEach((set) => {
      // sum all sessions per step
      stepSummaries.push(set.data.reduce((a, b) => a + b, 0));
    });

    chartData = {
      labels: chartDataLabels,
      datasets: datasets
    };
  }

  onMount(() => {
    steps = [funnel.steps[0], ...funnel.steps.filter((step) => step.goal)];
    reload();
  });
</script>

<div class="padding-45 f-left col20" style="padding-top:0;">
  <div class="flex col20 f-left store-section-analytics-warapper narrow-analytics-boxes flex-col-mobile" style="background: rgb(245, 246, 250); margin-top: 0px;">
    <div class="analytics-box analytics-box-small ">
      <h3 class="flex align-center">{M.TOTAL_VISITORS} <span class="chart-color ml-10" style="background: #f97f7f" /></h3>
      <h2 class="col20 f-left f-28 gray-text">
        <b>
          {stepSummaries[0] || 0}
        </b>
      </h2>
    </div>
    <div class="analytics-box analytics-box-small analytics-box-two-third">
      <h3 class="flex align-center">{M.REACHED_GOAL}</h3>
      <h2 class="col20 f-left f-28 gray-text">
        {#each chartData.datasets as set, setIdx}
          {#if setIdx > 0}
            <span class="col20 f-left ml-0 mb-5">
              {steps[setIdx].name} - <b>{stepSummaries[setIdx]}</b> ({parsePercent(stepSummaries[setIdx] / stepSummaries[0])})
              <span class="chart-color ml-10" style={"background: " + gradientColors[setIdx][0]} />
            </span>
          {/if}
        {/each}
      </h2>
    </div>
  </div>
  <div class="flex f-left col20 mt-25 flex-col-mobile stats-wrapper">
    <div class="analytics-box-big no-bpadding-mobile col20">
      <h2 class="col20 f-left f-28 gray-text t-left">{M.VISITORS_GOALS}</h2>

      <div class="col20 f-left relative mt-25 modern-chart-box" bind:this={chartWrapper}>
        <Chart data={chartData} options={chartOptions} />
        <div class="axis">
          {#each chartDataLabels as label, i}
            <div class="tick">
              <span class="tick-label">{label}</span>
              {#each chartData.datasets as set, setIdx}
                <span class="value flex align-center with-color" style={"top: " + (15 + setIdx * 38) + "px"}>
                  <span class="chart-color" style={"background: " + gradientColors[setIdx][0]} />
                  {set.data[i]}
                </span>
              {/each}
            </div>
          {/each}
        </div>
        {#if grouping === "daily" && showMonthsBelow}
          <div class="col20 f-left flex flex-row chart-label-row">
            {#each monthLabels as label, i}
              <span
                class="flex-1"
                class:ta-left={i === 0 && monthLabels.length > 1}
                class:ta-right={i === monthLabels.length - 1 && monthLabels.length > 1}
                class:ta-center={monthLabels.length === 1 || (i > 1 && i < monthLabels.length - 1)}>
                <p>{label}</p>
              </span>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>
